// Route planning on a simplified network of German motorways and main roads (content/roads.json).
// Fastest route by driving time (Dijkstra); start and end join the network over local roads at their two
// nearest junctions. Distances: straight line × detour factor of the road class; speeds include typical traffic.
import { haversineKm } from './util.js';
import { ROADS } from './content.js';

var NODES = {}, ADJ = {}, CACHE = {};

export function initRoads() {
  NODES = ROADS.nodes; ADJ = {}; CACHE = {};
  Object.keys(NODES).forEach(function (k) { ADJ[k] = []; });
  ROADS.roads.forEach(function (r) {
    for (var i = 1; i < r.via.length; i++) link(r.via[i - 1], r.via[i], r.name, r.class);
  });
}
function link(a, b, name, cls) {
  var A = NODES[a], B = NODES[b], km = haversineKm(A.lat, A.lon, B.lat, B.lon) * ROADS.detour[cls], h = km / ROADS.speeds[cls];
  ADJ[a].push({ to: b, km: km, h: h, road: name, cls: cls }); ADJ[b].push({ to: a, km: km, h: h, road: name, cls: cls });
}
function nearest(p, n) {
  return Object.keys(NODES).map(function (k) { return { k: k, d: haversineKm(p.lat, p.lon, NODES[k].lat, NODES[k].lon) }; })
    .sort(function (a, b) { return a.d - b.d; }).slice(0, n);
}
function local(d) { var km = d * ROADS.detour.L; return { km: km, h: km / ROADS.speeds.L }; }

// fastest route between two places ({id, lat, lon, name}); roads named in `avoid` are used only when there is
// no other way (25 × their time). Results are cached.
export function route(from, to, avoid) {
  avoid = avoid && avoid.length ? avoid : null;
  var key = (from.id || from.lat + ',' + from.lon) + '>' + (to.id || to.lat + ',' + to.lon) + (avoid ? '|' + avoid.join(',') : '');
  if (CACHE[key]) return CACHE[key];
  var direct = haversineKm(from.lat, from.lon, to.lat, to.lon), best = null;
  if (direct < 25) { var l0 = local(direct); best = { km: l0.km, drive: l0.h, legs: [{ road: 'local roads', cls: 'L', km: l0.km, h: l0.h }], pts: [[from.lat, from.lon], [to.lat, to.lon]], via: [] }; }
  var starts = nearest(from, 2), ends = nearest(to, 2), dist = {}, prev = {}, done = {};
  starts.forEach(function (s) { var l = local(s.d); if (dist[s.k] == null || l.h < dist[s.k]) { dist[s.k] = l.h; prev[s.k] = { from: null, road: 'local roads', cls: 'L', km: l.km, h: l.h }; } });
  // Dijkstra on driving time (the network is small: a linear scan for the next node is enough)
  for (;;) {
    var u = null; Object.keys(dist).forEach(function (k) { if (!done[k] && (u === null || dist[k] < dist[u])) u = k; });
    if (u === null) break; done[u] = true;
    ADJ[u].forEach(function (e) { var nd = dist[u] + e.h * (avoid && avoid.indexOf(e.road) >= 0 ? 25 : 1); if (dist[e.to] == null || nd < dist[e.to]) { dist[e.to] = nd; prev[e.to] = { from: u, road: e.road, cls: e.cls, km: e.km, h: e.h }; } });
  }
  ends.forEach(function (e) {
    if (dist[e.k] == null) return;
    var l = local(e.d), total = dist[e.k] + l.h;
    if (best && (best.cost != null ? best.cost : best.drive) <= total) return;
    var chain = [], k = e.k; while (k) { chain.unshift(k); k = prev[k].from; }
    var legs = [], pts = [[from.lat, from.lon]];
    chain.forEach(function (n) { var pv = prev[n]; legs.push({ road: pv.road, cls: pv.cls, km: pv.km, h: pv.h, to: n }); pts.push([NODES[n].lat, NODES[n].lon]); });
    legs.push({ road: 'local roads', cls: 'L', km: l.km, h: l.h }); pts.push([to.lat, to.lon]);
    best = { km: legs.reduce(function (a, x) { return a + x.km; }, 0), drive: total, cost: total, legs: legs, pts: pts, via: chain.map(function (n) { return NODES[n].name; }) };
  });
  if (!best) { var lx = local(direct); best = { km: lx.km, drive: lx.h, legs: [{ road: 'local roads', cls: 'L', km: lx.km, h: lx.h }], pts: [[from.lat, from.lon], [to.lat, to.lon]], via: [] }; }
  best.drive = best.legs.reduce(function (a, x) { return a + x.h; }, 0);
  best.avoided = avoid ? best.legs.some(function (x) { return avoid.indexOf(x.road) >= 0; }) : false;
  // roads in order, the same road counted once per stretch
  var roads = []; best.legs.forEach(function (x) { if (x.cls !== 'L' && roads[roads.length - 1] !== x.road) roads.push(x.road); });
  var mot = best.legs.filter(function (x) { return x.cls === 'A'; }).reduce(function (a, x) { return a + x.km; }, 0);
  best.roads = roads; best.motorwayShare = best.km ? mot / best.km : 0;
  best.h = best.drive + ROADS.handoverHours;      // one way, including loading and handover on site
  best.back = 2 * best.drive + ROADS.handoverHours; // there and back
  best.from = from; best.to = to;
  return (CACHE[key] = best);
}

export function roadNet() { return { nodes: NODES, roads: ROADS.roads }; }
export function routeText(r) { return (r.roads.length ? r.roads.join(' → ') : 'local roads') + (r.via.length > 2 ? ' via ' + r.via.filter(function (x, i, a) { return i > 0 && i < a.length - 1; }).slice(0, 3).join(', ') : ''); }
export function hm(h) { var m = Math.round(h * 60); return Math.floor(m / 60) + ':' + ('0' + (m % 60)).slice(-2); }
